import type { ICustomerQuery } from "@repo/shopify/queries/customer-gql";
import type { ICaaCustomer } from "@repo/shopify/queries/customer-account-gql";
import type { CustomerAccessToken } from "@shopify/hydrogen-react/storefront-api-types";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

/** Customer Account API(OAuth) 토큰. jp locale 전용 — classic `token`과 별개로 관리 */
export interface ICaaToken {
  accessToken: string;
  /** "hiko" 세션은 refresh_token을 Hiko 브로커가 서버사이드로 보관하므로 빈 문자열 */
  refreshToken: string;
  /** "hiko" 세션은 id_token을 내려주지 않으므로 빈 문자열 */
  idToken: string;
  /** ISO 문자열. Date.now() + expires_in * 1000 */
  expiresAt: string;
  /**
   * 토큰 발급 경로. "hiko" = HIKO Social Login Plus(signin-headless) 위젯을 통해 발급받아
   * 갱신/로그아웃을 위젯에 위임해야 함. "native"는 자체 PKCE 플로우(현재 제거됨, jp가 Custom(Hiko)
   * identity provider로 전환되며 더 이상 신규 발급되지 않음)로 만들어진 레거시 세션의 흔적 — 이미
   * 로그인한 기존 사용자의 persist된 store에 남아있을 수 있어 타입은 유지.
   */
  source?: "native" | "hiko";
}

export interface ICustomerStore {
  token: CustomerAccessToken;
  caaToken: ICaaToken | null;
  customer: ICustomerQuery["customer"];
  /** Customer Account API(jp)로 조회한 원본 customer. classic `customer`와 스키마가 달라 별도 보관 */
  caaCustomer: ICaaCustomer | null;
  /**
   * Hiko 로그인 시 어떤 provider(Google/Apple/email 등)를 썼는지. Shopify customer에 태깅되지 않아
   * (SocialLoginCaa.tsx가 위젯 클릭 시점에 캡처한) best-effort 값 — 이 브라우저에서 로그인한
   * 세션 기준이라 다른 기기의 로그인 이력은 반영 안 됨. logout 시 초기화.
   */
  caaLoginProvider: string | null;
  countryCode: string;

  login: (token: CustomerAccessToken) => void;
  loginCaa: (caaToken: ICaaToken) => void;
  logout: () => void;
  update: (customer: ICustomerQuery["customer"]) => void;
  updateCaa: (caaCustomer: ICaaCustomer) => void;
  setCaaLoginProvider: (caaLoginProvider: string | null) => void;
  setCountryCode: (countryCode: string) => void;
}

const initCustomer = {
  token: {
    accessToken: "",
    expiresAt: "",
  },
  caaToken: null as ICaaToken | null,
  caaCustomer: null as ICaaCustomer | null,
  caaLoginProvider: null as string | null,
  customer: {
    acceptsMarketing: false,
    email: "",
    firstName: "",
    id: "",
    lastName: "",
    phone: "",
    tags: [],
    metafields: [],
    addresses: {
      edges: [],
      nodes: [],
      pageInfo: {
        endCursor: undefined,
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: undefined,
      },
    },
    numberOfOrders: "",
    createdAt: "",
    displayName: "",
    orders: {
      edges: [],
      nodes: [],
      pageInfo: {
        endCursor: undefined,
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: undefined,
      },
      totalCount: "",
    },
    gender: undefined,
    birth: undefined,
    wishlist: undefined,
    updatedAt: "",
  },
  countryCode: "",
};

const localLang = localStorage.getItem("i18nextLng") || "ko";

export const useCustomerStore = create<ICustomerStore>()(
  persist(
    (set) => ({
      // 초기값
      ...initCustomer,

      // 함수
      login: (token: CustomerAccessToken) => {
        set(() => ({ token }));
      },
      loginCaa: (caaToken: ICaaToken) => {
        set(() => ({ caaToken }));
      },
      logout: () => {
        set(() => initCustomer);
      },
      update: (customer: ICustomerQuery["customer"]) => {
        set((state) => ({ customer: { ...state.customer, ...customer } }));
      },
      updateCaa: (caaCustomer: ICaaCustomer) => {
        set(() => ({ caaCustomer }));
      },
      setCaaLoginProvider: (caaLoginProvider: string | null) => {
        set(() => ({ caaLoginProvider }));
      },
      setCountryCode: (countryCode: string) => {
        set(() => ({ countryCode }));
      },
    }),
    {
      name: `${localLang}-customer`,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
